import { ref, set, onValue, off, get, remove } from "firebase/database";
import { database, ensureSignedIn } from "../config/firebase";
import { encodeState, decodeState } from "./stateManager";

const ensureDatabase = () => {
  if (!database) {
    console.warn("Firebase is not configured. TV sync is disabled.");
    return false;
  }
  return true;
};

// Keep writes for each tournament in invocation order. Without this queue,
// rapid score changes can resolve out of order and an older snapshot can
// overwrite a newer one in Firebase.
const tournamentSaveQueues = new Map();

// Generate a unique tournament ID
export const generateTournamentId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
};

// Save tournament state to Firebase
const persistTournamentToFirebase = async (tournamentId, state) => {
  try {
    if (!ensureDatabase()) return false;
    const user = await ensureSignedIn();
    if (!user) return false;
    const encoded = encodeState(state);
    const tournamentRef = ref(database, `tournaments/${tournamentId}`);
    await set(tournamentRef, {
      state: encoded,
      lastUpdated: Date.now(),
      name: state.name,
      isComplete: state.isComplete || false
    });
    return true;
  } catch (error) {
    console.error("Error saving to Firebase:", error);
    return false;
  }
};

export const saveTournamentToFirebase = (tournamentId, state) => {
  const previousSave = tournamentSaveQueues.get(tournamentId) || Promise.resolve();
  const queuedSave = previousSave
    .catch(() => false)
    .then(() => persistTournamentToFirebase(tournamentId, state));

  tournamentSaveQueues.set(tournamentId, queuedSave);
  queuedSave.then(() => {
    if (tournamentSaveQueues.get(tournamentId) === queuedSave) {
      tournamentSaveQueues.delete(tournamentId);
    }
  });

  return queuedSave;
};

// Load tournament state from Firebase (one-time read)
export const loadTournamentFromFirebase = async (tournamentId) => {
  try {
    if (!ensureDatabase()) return null;
    const user = await ensureSignedIn();
    if (!user) return null;
    const tournamentRef = ref(database, `tournaments/${tournamentId}`);
    const snapshot = await get(tournamentRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      return decodeState(data.state);
    }
    return null;
  } catch (error) {
    console.error("Error loading from Firebase:", error);
    return null;
  }
};

// Subscribe to real-time tournament updates
export const subscribeTournamentUpdates = (tournamentId, callback) => {
  let cancelled = false;
  let unsubscribe = () => {};

  const start = async () => {
    if (!ensureDatabase()) {
      callback(null);
      return;
    }
    const user = await ensureSignedIn();
    if (!user) {
      callback(null);
      return;
    }
    if (cancelled) return;
    const tournamentRef = ref(database, `tournaments/${tournamentId}`);

    onValue(
      tournamentRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const state = decodeState(data.state);
          callback(state);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error("Error subscribing to Firebase updates:", error);
        callback(null);
      }
    );

    unsubscribe = () => off(tournamentRef);
  };

  start();

  return () => {
    cancelled = true;
    unsubscribe();
  };
};

// Check if tournament exists in Firebase
export const tournamentExistsInFirebase = async (tournamentId) => {
  try {
    if (!ensureDatabase()) return false;
    const user = await ensureSignedIn();
    if (!user) return false;
    const tournamentRef = ref(database, `tournaments/${tournamentId}`);
    const snapshot = await get(tournamentRef);
    return snapshot.exists();
  } catch (error) {
    console.error("Error checking tournament existence:", error);
    return false;
  }
};

// Clean up old tournaments from Firebase (older than specified days)
export const cleanupOldTournaments = async (daysOld = 30) => {
  try {
    if (!ensureDatabase()) return { success: false, error: "Firebase not configured" };
    const user = await ensureSignedIn();
    if (!user) return { success: false, error: "Firebase auth unavailable" };
    const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
    const tournamentsRef = ref(database, 'tournaments');
    const snapshot = await get(tournamentsRef);
    
    if (!snapshot.exists()) {
      return { success: true, deleted: 0 };
    }

    let deletedCount = 0;
    const tournaments = snapshot.val();
    
    for (const [tournamentId, data] of Object.entries(tournaments)) {
      if (data.lastUpdated && data.lastUpdated < cutoffTime) {
        const tournamentRef = ref(database, `tournaments/${tournamentId}`);
        await remove(tournamentRef);
        deletedCount++;
      }
    }

    console.log(`Cleaned up ${deletedCount} old tournaments`);
    return { success: true, deleted: deletedCount };
  } catch (error) {
    console.error("Error cleaning up old tournaments:", error);
    return { success: false, error: error.message };
  }
};

// Delete a specific tournament from Firebase
export const deleteTournamentFromFirebase = async (tournamentId) => {
  try {
    if (!ensureDatabase()) return false;
    const user = await ensureSignedIn();
    if (!user) return false;
    const tournamentRef = ref(database, `tournaments/${tournamentId}`);
    await remove(tournamentRef);
    return true;
  } catch (error) {
    console.error("Error deleting tournament:", error);
    return false;
  }
};
